import React, { useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router-dom';


dayjs.extend(utc);
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Row,
  Spin,
  Statistic,
  Table,
  Typography,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckSquare,
  DollarSign,
  FileText,
  TrendingUp,
  Phone,
  Rocket,
  Users,
  Trophy,
} from 'lucide-react';
import { useBusinessOverview } from '../hooks/useBusinessOverview';
import type {
  BusinessOverviewFunnelBreakdownStage,
  GetBusinessOverviewParams,
} from '../api/businessOverview';
import BusinessOverviewFilterBar, {
  defaultMonthRange,
  type BusinessOverviewFilterState,
} from './BusinessOverviewFilterBar';
import BusinessOverviewFunnelChart from './BusinessOverviewFunnelChart';
import BusinessOverviewMetricCard, { formatAmount } from './BusinessOverviewMetricCard';
import BusinessOverviewSection from './BusinessOverviewSection';
import PageScaffold from '../../../components/PageScaffold';
import { usePermission } from '../../permissions/PermissionsContext';
import { useUrlListFilter } from '../../../lib/query/useUrlListFilter';
import { DASHBOARD_FILTER_FIELDS } from '../../../routes/filterMaps';
import { withDashboardContext, type DashboardFilterContext } from '../../../routes/filterNavigation';
import { LEAD_FILTER_FIELDS, TASK_FILTER_FIELDS, CONTACT_FILTER_FIELDS, DOCUMENT_FILTER_FIELDS, CAMPAIGN_FILTER_FIELDS } from '../../../routes/filterMaps';

const { Text } = Typography;

const cardBodyPadding = { padding: '16px 20px' };

const funnelColumns: ColumnsType<BusinessOverviewFunnelBreakdownStage> = [
  { title: 'Stage', dataIndex: 'stageLabel', key: 'stageLabel', ellipsis: true },
  { title: 'Deals', dataIndex: 'dealCount', key: 'dealCount', width: 72, align: 'right' as const },
  {
    title: 'Targeted',
    dataIndex: 'totalAmount',
    key: 'totalAmount',
    width: 100,
    align: 'right' as const,
    render: (v: number) => formatAmount(v),
  },
  {
    title: 'Weighted',
    dataIndex: 'weightedAmount',
    key: 'weightedAmount',
    width: 100,
    align: 'right' as const,
    render: (v: number) => formatAmount(v),
  },
  {
    title: 'Conv.',
    dataIndex: 'conversionPercentFromPrevious',
    key: 'conversion',
    width: 72,
    align: 'right' as const,
    render: (v: number | null) => (v == null ? '—' : `${v.toFixed(1)}%`),
  },
];

function DashboardBody({ children }: { children: React.ReactNode }) {
  return <Row gutter={[16, 16]}>{children}</Row>;
}

function DashboardShell({
  filters,
  onFiltersChange,
  hasAssigneeFilter,
  children,
}: {
  filters: BusinessOverviewFilterState;
  onFiltersChange: (value: BusinessOverviewFilterState) => void;
  hasAssigneeFilter?: boolean;
  children: React.ReactNode;
}) {
  return (
    <PageScaffold
      title="Business Overview"
      subtitle="Period KPIs, pipeline funnel, and activity metrics for your tenant."
      filters={<BusinessOverviewFilterBar value={filters} onChange={onFiltersChange} hasAssigneeFilter={hasAssigneeFilter} />}
    >
      <DashboardBody>{children}</DashboardBody>
    </PageScaffold>
  );
}

const BusinessOverviewDashboard: React.FC = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const isAdmin = usePermission('administrator.access');
  const defaultFilters: BusinessOverviewFilterState = useMemo(() => ({
    dateRange: defaultMonthRange(),
    segmentIds: [],
    contactCategoryIds: [],
    assigneeIds: [],
  }), []);

  const transformOptions = useMemo(() => ({
    transform: {
      deserialize: (params: URLSearchParams) => {
        const from = params.get('fromUtc');
        const to = params.get('toUtc');
        if (from && to) {
          return {
            dateRange: [dayjs(from), dayjs(to)] as [Dayjs, Dayjs],
          } as Partial<BusinessOverviewFilterState>;
        }
        return {};
      },
      serialize: (f: BusinessOverviewFilterState) => {
        return {
          fromUtc: f.dateRange[0].utc().startOf('day').toISOString(),
          toUtc: f.dateRange[1].utc().startOf('day').toISOString(),
        };
      },
    },
  }), []);

  const [filters, setFilters] = useUrlListFilter(
    defaultFilters,
    DASHBOARD_FILTER_FIELDS,
    transformOptions,
  );

  const queryParams = useMemo((): GetBusinessOverviewParams => {
    const [from, to] = filters.dateRange;
    const params: GetBusinessOverviewParams = {
      fromUtc: from.utc().startOf('day').toISOString(),
      toUtc: to.utc().startOf('day').toISOString(),
    };
    if (filters.segmentIds.length > 0) {
      params.segmentIds = filters.segmentIds;
    }
    if (filters.contactCategoryIds.length > 0) {
      params.contactCategoryIds = filters.contactCategoryIds;
    }
    if (isAdmin && filters.assigneeIds.length > 0) {
      params.assigneeIds = filters.assigneeIds;
    }
    return params;
  }, [filters, isAdmin]);

  const navTo = (
    path: string,
    overrides: Record<string, unknown>,
    fields: readonly import('../../../routes/filterMaps').FilterFieldDef[],
    forwardDateRange = path !== '/tasks',
  ) => {
    const ctx: DashboardFilterContext = {};
    if (forwardDateRange) {
      const [from, to] = filters.dateRange;
      ctx.fromUtc = from.utc().startOf('day').toISOString();
      ctx.toUtc = to.utc().startOf('day').toISOString();
    }
    if (filters.segmentIds.length > 0) ctx.segmentIds = filters.segmentIds;
    if (filters.contactCategoryIds.length > 0) ctx.contactCategoryIds = filters.contactCategoryIds;
    if (filters.assigneeIds.length > 0) ctx.assigneeIds = filters.assigneeIds;
    navigate(withDashboardContext(path, ctx, overrides, fields));
  };
  const { data, isLoading, isError, error, refetch } = useBusinessOverview(queryParams);

  if (isLoading && data === undefined) {
    return (
        <DashboardShell filters={filters} onFiltersChange={setFilters} hasAssigneeFilter={isAdmin}>
        <Col span={24}>
          <Flex justify="center" align="center" style={{ minHeight: '50vh' }}>
            <Spin size="large" />
          </Flex>
        </Col>
      </DashboardShell>
    );
  }

  if (isError) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to load business overview data.';
    return (
      <DashboardShell filters={filters} onFiltersChange={setFilters} hasAssigneeFilter={isAdmin}>
        <Col span={24}>
          <Alert
            type="error"
            message="Error loading dashboard"
            description={errorMessage}
            showIcon
            action={
              <Button onClick={() => refetch()} type="primary" size="small">
                Retry
              </Button>
            }
          />
        </Col>
      </DashboardShell>
    );
  }

  if (!data) {
    return (
      <DashboardShell filters={filters} onFiltersChange={setFilters} hasAssigneeFilter={isAdmin}>
        <Col span={24}>
          <Empty description="No data available." />
        </Col>
      </DashboardShell>
    );
  }

  const sectionCardStyle = {
    height: '100%',
    background: token.colorBgContainer,
    borderRadius: token.borderRadiusLG,
  };

  return (
    <DashboardShell filters={filters} onFiltersChange={setFilters} hasAssigneeFilter={isAdmin}>
      <BusinessOverviewSection
        title="Pipeline & outcomes"
        subtitle="Open pipeline is a snapshot at period end. Won and lost use closing dates in the selected range."
      >
        <Col xs={24} md={8}>
          <BusinessOverviewMetricCard
            title="Open pipeline"
            count={data.leadSummary.open.count}
            amount={data.leadSummary.open.amount}
            icon={<TrendingUp size={18} />}
            onClick={() => navTo('/leads', { closingStatus: 0 }, LEAD_FILTER_FIELDS)}
          />
        </Col>
        <Col xs={24} md={8}>
          <BusinessOverviewMetricCard
            title="Owned (won)"
            count={data.leadSummary.won.count}
            amount={data.leadSummary.won.amount}
            accent="success"
            icon={<Trophy size={18} />}
            onClick={() => navTo('/leads', { closingStatus: 2 }, LEAD_FILTER_FIELDS)}
          />
        </Col>
        <Col xs={24} md={8}>
          <BusinessOverviewMetricCard
            title="Lost"
            count={data.leadSummary.lost.count}
            amount={data.leadSummary.lost.amount}
            accent="error"
            onClick={() => navTo('/leads', { closingStatus: 3 }, LEAD_FILTER_FIELDS)}
          />
        </Col>
      </BusinessOverviewSection>

      <BusinessOverviewSection
        title="Referrals"
        subtitle="Open counts are snapshot; won and lost are for the selected period."
      >
        <Col xs={24} sm={12} lg={6}>
          <BusinessOverviewMetricCard
            title="Referral · open"
            count={data.referralSummary.referral.count}
            amount={data.referralSummary.referral.amount}
            onClick={() => navTo('/leads', { hasReferral: true, closingStatus: 0 }, LEAD_FILTER_FIELDS)}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <BusinessOverviewMetricCard
            title="Non-referral · open"
            count={data.referralSummary.nonReferral.count}
            amount={data.referralSummary.nonReferral.amount}
            onClick={() => navTo('/leads', { hasReferral: false, closingStatus: 0 }, LEAD_FILTER_FIELDS)}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <BusinessOverviewMetricCard
            title="Referral · won"
            count={data.referralSummary.referralWon.count}
            amount={data.referralSummary.referralWon.amount}
            accent="success"
            onClick={() => navTo('/leads', { hasReferral: true, closingStatus: 2 }, LEAD_FILTER_FIELDS)}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <BusinessOverviewMetricCard
            title="Referral · lost"
            count={data.referralSummary.referralLost.count}
            amount={data.referralSummary.referralLost.amount}
            accent="error"
            onClick={() => navTo('/leads', { hasReferral: true, closingStatus: 3 }, LEAD_FILTER_FIELDS)}
          />
        </Col>
      </BusinessOverviewSection>

      <BusinessOverviewSection
        title="Contacts & documents"
        subtitle="Contact and agent totals reflect the current base and ignore the date range."
      >
        <Col xs={24} md={8}>
          <BusinessOverviewMetricCard
            title="Total contacts"
            count={data.contacts.totalContacts}
            icon={<Users size={18} />}
            onClick={() => navTo('/contacts', {}, CONTACT_FILTER_FIELDS, false)}
            hint="All contacts in system"
          />
        </Col>
        <Col xs={24} md={8}>
          <BusinessOverviewMetricCard
            title="Agents"
            count={data.contacts.totalAgents}
            icon={<Users size={18} />}
            onClick={() => navTo('/contacts', { role: 0 }, CONTACT_FILTER_FIELDS, false)}
            hint="Contacts with agent flag"
          />
        </Col>
        <Col xs={24} md={8}>
          <BusinessOverviewMetricCard
            title="Accepted in period"
            count={data.documents.acceptedInPeriod}
            icon={<FileText size={18} />}
            onClick={() => navTo('/documents', { status: 2 }, DOCUMENT_FILTER_FIELDS)}
            hint={`Expiring within 30 days: ${data.documents.expiringWithin30Days}`}
          />
        </Col>
      </BusinessOverviewSection>

      <BusinessOverviewSection
        title="Marketing & commissions"
        subtitle="Campaign metrics respect segment and category filters."
      >
        <Col xs={24} lg={12}>
          <Card
            bordered={false}
            title="Campaigns"
            extra={<Rocket size={18} style={{ color: token.colorTextSecondary }} />}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
            className="task-card-clickable"
            onClick={() => navTo('/campaigns', { status: 1 }, CAMPAIGN_FILTER_FIELDS)}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Statistic title="Active campaigns" value={data.campaigns.totalActiveCampaigns} valueStyle={{ fontSize: 24, fontWeight: 700 }} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Current status · org-wide
                </Text>
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Budget (period overlap)"
                  value={formatAmount(data.campaigns.totalBudget)}
                  valueStyle={{ fontSize: 24, fontWeight: 700 }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Goal completion"
                  value={data.campaigns.targetCompletionRatio}
                  suffix="%"
                  precision={1}
                  valueStyle={{ fontSize: 24, fontWeight: 700 }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            bordered={false}
            title="Commissions"
            extra={<DollarSign size={18} style={{ color: token.colorTextSecondary }} />}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Period total"
                  value={formatAmount(data.commissions.periodTotal)}
                  valueStyle={{ fontSize: 24, fontWeight: 700 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="All-time total"
                  value={formatAmount(data.commissions.allTimeTotal)}
                  valueStyle={{ fontSize: 24, fontWeight: 700 }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </BusinessOverviewSection>

      <BusinessOverviewSection title="Operations">
        <Col xs={24} lg={14}>
          <Card
            bordered={false}
            title="Tasks"
            extra={<CheckSquare size={18} style={{ color: token.colorTextSecondary }} />}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <div style={{ cursor: 'pointer' }} onClick={() => navTo('/tasks', { statusFilter: 0 }, TASK_FILTER_FIELDS)}>
                  <Statistic title="Open" value={data.tasks.openTasks} valueStyle={{ fontSize: 24, fontWeight: 700 }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Snapshot
                  </Text>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{ cursor: 'pointer' }} onClick={() => navTo('/tasks', { statusFilter: 3 }, TASK_FILTER_FIELDS)}>
                  <Statistic title="Completed" value={data.tasks.completedTasks} valueStyle={{ fontSize: 24, fontWeight: 700 }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    In period
                  </Text>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{ cursor: 'pointer' }} onClick={() => navTo('/tasks', { statusFilter: 3 }, TASK_FILTER_FIELDS)}>
                  <Statistic
                    title="On time"
                    value={data.tasks.completedOnTime}
                    valueStyle={{ color: token.colorSuccess, fontSize: 24, fontWeight: 700 }}
                  />
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{ cursor: 'pointer' }} onClick={() => navTo('/tasks', { statusFilter: 3, completedLate: true }, TASK_FILTER_FIELDS)}>
                  <Statistic
                    title="Late"
                    value={data.tasks.completedAfterDueDate}
                    valueStyle={{ color: data.tasks.completedAfterDueDate > 0 ? token.colorWarning : undefined, fontSize: 24, fontWeight: 700 }}
                  />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Card
            bordered={false}
            title="Follow-ups"
            extra={<Phone size={18} style={{ color: token.colorTextSecondary }} />}
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ cursor: 'pointer' }} onClick={() => navTo('/tasks', { statusFilter: 0, taskTypeFilter: 1 }, TASK_FILTER_FIELDS)}>
                  <Statistic
                    title="Open"
                    value={data.followUps.openFollowUps}
                    valueStyle={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: data.followUps.openFollowUps > 0 ? token.colorPrimary : undefined,
                    }}
                  />
                </div>
              </Col>
              <Col span={24}>
                <div style={{ cursor: 'pointer' }} onClick={() => navTo('/tasks', { statusFilter: 3, taskTypeFilter: 1 }, TASK_FILTER_FIELDS)}>
                  <Statistic title="Completed in period" value={data.followUps.completedInPeriod} valueStyle={{ fontSize: 24, fontWeight: 700 }} />
                </div>
              </Col>
              <Col span={24}>
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => navTo(
                    '/tasks',
                    {
                      statusFilter: 0,
                      taskTypeFilter: 1,
                      dueFrom: dayjs().utc().format('YYYY-MM-DD'),
                      dueTo: dayjs().utc().add(7, 'day').format('YYYY-MM-DD'),
                    },
                    TASK_FILTER_FIELDS,
                    false,
                  )}
                >
                  <Statistic
                    title="Upcoming (7 days)"
                    value={data.followUps.upcomingIn7Days}
                    valueStyle={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: data.followUps.upcomingIn7Days > 0 ? token.colorPrimary : undefined,
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Open · due by {dayjs().utc().add(7, 'day').format('MMM D, YYYY')} (UTC)
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Card
            bordered={false}
            title="BANT quality"
            style={sectionCardStyle}
            styles={{ body: cardBodyPadding }}
            className="task-card-clickable"
            onClick={() => navTo('/leads', {}, LEAD_FILTER_FIELDS)}
          >
            <Statistic title="Average score" value={data.bant.averageScore} precision={1} valueStyle={{ fontSize: 24, fontWeight: 700 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {data.bant.scoredLeadCount} scored open leads
            </Text>
          </Card>
        </Col>
      </BusinessOverviewSection>

      <BusinessOverviewSection
        title="Pipeline funnel"
        subtitle="Open deals at period end, grouped by pipeline stage."
      >
        <Col xs={24} xl={14}>
          <Card
            bordered={false}
            title="Stage distribution"
            style={{ ...sectionCardStyle, minHeight: 360 }}
            styles={{ body: { ...cardBodyPadding, minHeight: 300 } }}
          >
            <BusinessOverviewFunnelChart
              stages={data.pipelineFunnel}
              isLoading={false}
              isError={false}
            />
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card
            bordered={false}
            title="Stage breakdown"
            style={{ ...sectionCardStyle, minHeight: 360 }}
            styles={{ body: cardBodyPadding }}
          >
            <Table<BusinessOverviewFunnelBreakdownStage>
              rowKey="stageId"
              columns={funnelColumns}
              dataSource={data.funnelBreakdown}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No open pipeline in this view' }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </Col>
      </BusinessOverviewSection>
    </DashboardShell>
  );
};

export default BusinessOverviewDashboard;
